import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Info,
  Award,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Lightbulb,
  TrendingUp
} from 'lucide-react';

const Step4Enhanced = ({ formData, setFormData, onNext, onPrev }) => {
  const [errors, setErrors] = useState({});
  const [hasExperience, setHasExperience] = useState(
    formData.hasAffiliateExperience !== undefined ? formData.hasAffiliateExperience : null
  );
  const [previousPlatforms, setPreviousPlatforms] = useState(
    formData.previousAffiliatePlatforms || ''
  );
  const [motivation, setMotivation] = useState(
    formData.motivationToJoin || ''
  );

  const handleExperienceChange = (value) => {
    setHasExperience(value);
    if (!value) {
      setPreviousPlatforms('');
    }
    if (errors.hasAffiliateExperience) {
      setErrors(prev => ({ ...prev, hasAffiliateExperience: '' }));
    }
  };

  const handlePlatformsChange = (value) => {
    setPreviousPlatforms(value);
    if (errors.previousAffiliatePlatforms) {
      setErrors(prev => ({ ...prev, previousAffiliatePlatforms: '' }));
    }
  };

  const handleMotivationChange = (value) => {
    setMotivation(value);
    if (errors.motivationToJoin) {
      setErrors(prev => ({ ...prev, motivationToJoin: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (hasExperience === null) {
      newErrors.hasAffiliateExperience = 'Please indicate if you have affiliate marketing experience';
    }

    if (!motivation?.trim()) {
      newErrors.motivationToJoin = 'Please tell us why you want to join';
    } else if (motivation.trim().length < 20) {
      newErrors.motivationToJoin = 'Please provide a more detailed response (at least 20 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setFormData(prev => ({
        ...prev,
        hasAffiliateExperience: hasExperience,
        previousAffiliatePlatforms: hasExperience ? previousPlatforms.trim() : null,
        motivationToJoin: motivation.trim()
      }));
      onNext();
    }
  };

  const motivationExamples = [
    'Earn extra income while sharing products I love',
    'Build my personal brand and grow my audience',
    'Learn digital marketing and business skills',
    'Create passive income streams',
    'Support local businesses and deals'
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="py-4 mx-auto w-full max-w-5xl"
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          D. EXPERIENCE & MOTIVATION
          <span className="ml-2 text-red-600">(MANDATORY)</span>
        </h2>
        <p className="text-gray-600">
          Help us understand your background and goals
        </p>
      </div>

      {/* UX Guidelines Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200"
      >
        <div className="flex items-start space-x-3">
          <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-600" />
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-semibold text-amber-900">
              Why we need this information:
            </h3>
            <div className="grid grid-cols-1 gap-2 text-xs text-amber-800 md:grid-cols-2">
              <div className="flex items-start space-x-2">
                <TrendingUp className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Filter unserious applicants - Ensure committed partners</span>
              </div>
              <div className="flex items-start space-x-2">
                <Lightbulb className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Identify learning needs - Provide targeted support</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Affiliate Experience */}
        <div>
          <div className="flex items-center mb-4 space-x-2">
            <Award className="w-5 h-5 text-gray-600" />
            <label className="text-base font-semibold text-gray-900">
              Affiliate Marketing Experience
              <span className="ml-1 text-red-600">*</span>
            </label>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            Have you done affiliate marketing before?
          </p>
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <motion.button
              type="button"
              onClick={() => handleExperienceChange(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                hasExperience === true
                  ? 'border-green-500 bg-green-50 shadow-md'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${
                    hasExperience === true ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <CheckCircle2 className={`w-6 h-6 ${
                      hasExperience === true ? 'text-green-600' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="text-left">
                    <p className={`text-lg font-semibold ${
                      hasExperience === true ? 'text-green-700' : 'text-gray-900'
                    }`}>
                      Yes
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      I have experience with affiliate marketing
                    </p>
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => handleExperienceChange(false)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                hasExperience === false
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${
                    hasExperience === false ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <XCircle className={`w-6 h-6 ${
                      hasExperience === false ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="text-left">
                    <p className={`text-lg font-semibold ${
                      hasExperience === false ? 'text-blue-700' : 'text-gray-900'
                    }`}>
                      No
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      I'm new to affiliate marketing
                    </p>
                  </div>
                </div>
              </div>
            </motion.button>
          </div>
          
          {errors.hasAffiliateExperience && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500"
            >
              {errors.hasAffiliateExperience}
            </motion.p>
          )}

          {/* Previous Platforms (conditional) */}
          {hasExperience === true && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-6"
            >
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Where have you done affiliate marketing before? (Optional)
              </label>
              <input
                type="text"
                value={previousPlatforms}
                onChange={(e) => handlePlatformsChange(e.target.value)}
                className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-all duration-200 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Amazon Associates, ShareASale, local affiliate programs"
              />
              <p className="mt-2 text-xs text-gray-500">
                List any platforms or programs you've worked with (optional but helpful)
              </p>
            </motion.div>
          )}

          {hasExperience === false && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="p-4 mt-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <div className="flex items-start space-x-2">
                <Lightbulb className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    No worries! We welcome beginners.
                  </p>
                  <p className="mt-1 text-xs text-blue-700">
                    We'll provide training and resources to help you succeed as an affiliate marketer. 
                    Your motivation and dedication matter more than experience.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Motivation */}
        <div>
          <div className="flex items-center mb-4 space-x-2">
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <label className="text-base font-semibold text-gray-900">
              Why Join Gwo Sevo Affiliate Network?
              <span className="ml-1 text-red-600">*</span>
            </label>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            Tell us why you want to join and what you hope to achieve
          </p>
          
          <textarea
            value={motivation}
            onChange={(e) => handleMotivationChange(e.target.value)}
            rows={5}
            className={`px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 resize-none placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
              errors.motivationToJoin ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Share your goals, interests, and what excites you about becoming an affiliate..."
          />
          
          <div className="flex justify-between items-center mt-2">
            <p className={`text-xs ${
              motivation.length < 20 ? 'text-gray-500' : 'text-green-600'
            }`}>
              {motivation.length} / 20 minimum characters
            </p>
            {motivation.length >= 20 && (
              <p className="text-xs text-green-600">✓ Good length</p>
            )}
          </div>
          
          {errors.motivationToJoin && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500"
            >
              {errors.motivationToJoin}
            </motion.p>
          )}

          {/* Motivation Examples */}
          <div className="p-4 mt-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="mb-3 text-xs font-medium text-gray-700">
              💡 Need inspiration? Here are some example motivations:
            </p>
            <ul className="space-y-2">
              {motivationExamples.map((example, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-gray-400">•</span>
                  <button
                    type="button"
                    onClick={() => handleMotivationChange(example)}
                    className="text-xs text-left text-gray-600 transition-colors hover:text-red-600"
                  >
                    "{example}"
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-gray-500 italic">
              Click any example to use it, or write your own unique response
            </p>
          </div>
        </div>
      </form>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <motion.button
          onClick={onPrev}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </motion.button>

        <motion.button
          onClick={handleSubmit}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-8 py-3 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40"
        >
          Continue
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Step4Enhanced;
